import { Check, ExternalLink, FileText, ScanText, X } from "lucide-react"
import { useState } from "react"

import {
  CAMPOS,
  type LecturaPersonal,
  marcadosDeEntrada,
  NOMBRE_ARCHIVO,
  type Propuesta,
  propuestaDe,
} from "@/componentes/personas/lectorPersonal"
import { NIVELES_ESTUDIO, type Persona } from "@/componentes/personas/persona"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"

const NIVEL = Object.fromEntries(NIVELES_ESTUDIO) as Record<string, string>

/**
 * Lo leído, para revisarlo antes de usarlo.
 *
 * ⚠ **Cada dato con su casilla, y a la vista lo que la ficha ya dice.** Sin la columna
 * «En la ficha», usar la lectura pisaría a ciegas lo que alguien corrigió a mano.
 *
 * ⚠ **Con un bloqueo no hay «Usar»**: son los documentos de otra persona, o no hay
 * ninguno legible. Si la cédula ya tiene ficha, se ofrece abrirla en vez de duplicarla.
 */
export function RevisionLectura({
  lectura,
  actual,
  onUsar,
  onIgnorar,
  onAbrirFicha,
}: {
  lectura: LecturaPersonal
  actual: (campo: keyof Persona) => string
  onUsar: (p: Propuesta) => void
  onIgnorar: () => void
  onAbrirFicha?: (id: number) => void
}) {
  const l = lectura.leido
  const [marcados, setMarcados] = useState(() => marcadosDeEntrada(l, actual))
  const [conEstudios, setConEstudios] = useState(l.estudios.length > 0)
  const [conExperiencias, setConExperiencias] = useState(l.experiencias.length > 0)
  const leidos = CAMPOS.filter((c) => l[c.leido])

  function alternar(campo: string) {
    setMarcados((previos) => {
      const nuevos = new Set(previos)
      if (nuevos.has(campo)) nuevos.delete(campo)
      else nuevos.add(campo)
      return nuevos
    })
  }

  return (
    <section className="space-y-4 text-sm" aria-label="Datos leídos de los documentos">
      <header className="flex flex-wrap items-center gap-2">
        <ScanText size={16} aria-hidden={true} style={{ color: "var(--primary)" }} />
        <h3 className="font-semibold">Leído de los documentos</h3>
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          · confianza {l.confianza.toLowerCase()}
        </span>
      </header>

      <ul className="flex flex-wrap gap-2" aria-label="Archivos identificados">
        {lectura.archivos.map((a, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: lista de sólo lectura que no se reordena
          <li key={`${i}-${a.nombre}`} className="flex min-w-0 items-center gap-1.5 text-xs">
            <FileText size={14} aria-hidden={true} />
            <span className="max-w-[12rem] truncate">{a.nombre}</span>
            <Insignia tono={a.tipo === "OTRO" ? "aviso" : "info"}>
              {NOMBRE_ARCHIVO[a.tipo]}
            </Insignia>
          </li>
        ))}
      </ul>

      {lectura.bloqueos.length > 0 && (
        <ul
          className="list-disc space-y-1 pl-5"
          style={{ color: "var(--destructive)" }}
          role="alert"
        >
          {lectura.bloqueos.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {lectura.ficha_existente_id !== null && onAbrirFicha && (
        <Boton tamano="sm" onClick={() => onAbrirFicha(lectura.ficha_existente_id as number)}>
          <ExternalLink size={14} /> Abrir la ficha que ya existe
        </Boton>
      )}

      {leidos.length > 0 && (
        <div className="overflow-x-auto">
          {/* ⚠ `table-fixed` con anchos declarados: con el reparto automático, una
              dirección larga se quedaba casi todo el ancho y «Dato» y «En la ficha» se
              partían en dos líneas. «Dato» lleva su ancho (cabe «Fecha de nacimiento»)
              y lo leído y lo de la ficha van a partes iguales, que es lo que se compara.
              El mínimo hace que en un teléfono la tabla se desplace dentro de su caja. */}
          <table className="w-full min-w-[34rem] table-fixed text-left text-sm">
            <colgroup>
              <col className="w-8" />
              <col className="w-40" />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr className="text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
                <th className="py-1 font-medium">
                  <span className="sr-only">Usar</span>
                </th>
                <th className="py-1 pr-3 font-medium">Dato</th>
                <th className="py-1 pr-3 font-medium">Leído</th>
                <th className="py-1 font-medium">En la ficha</th>
              </tr>
            </thead>
            <tbody>
              {leidos.map((c) => {
                const id = `usar-${c.ficha}`
                const enFicha = actual(c.ficha)
                return (
                  <tr key={c.ficha} className="align-top">
                    <td className="py-1">
                      <input
                        id={id}
                        type="checkbox"
                        className="h-4 w-4"
                        checked={marcados.has(c.ficha)}
                        onChange={() => alternar(c.ficha)}
                      />
                    </td>
                    <td
                      className="whitespace-nowrap py-1 pr-3"
                      style={{ color: "var(--fg-muted)" }}
                    >
                      <label htmlFor={id}>{c.rotulo}</label>
                    </td>
                    <td className="break-words py-1 pr-3 tabular-nums">{l[c.leido]}</td>
                    <td
                      className="break-words py-1 tabular-nums"
                      style={{ color: "var(--fg-muted)" }}
                    >
                      {enFicha || "—"}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {l.estudios.length > 0 && (
        <fieldset className="space-y-1">
          <label className="flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={conEstudios}
              onChange={(e) => setConEstudios(e.target.checked)}
            />
            Estudios ({l.estudios.length}) — reemplazan los de la ficha
          </label>
          <ul className="list-disc pl-10 text-xs" style={{ color: "var(--fg-muted)" }}>
            {l.estudios.map((e, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: lista de sólo lectura que no se reordena
              <li key={`${i}-${e.titulo}`}>
                {NIVEL[e.nivel] ?? e.nivel}: {e.titulo}
                {e.institucion ? ` · ${e.institucion}` : ""}
                {e.anio_fin ? ` · ${e.anio_fin}` : e.en_curso ? " · en curso" : ""}
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      {l.experiencias.length > 0 && (
        <fieldset className="space-y-1">
          <label className="flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={conExperiencias}
              onChange={(e) => setConExperiencias(e.target.checked)}
            />
            Experiencia laboral ({l.experiencias.length}) — reemplaza la de la ficha
          </label>
          <ul className="list-disc pl-10 text-xs" style={{ color: "var(--fg-muted)" }}>
            {l.experiencias.map((x, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: lista de sólo lectura que no se reordena
              <li key={`${i}-${x.empresa}`}>
                {x.cargo ? `${x.cargo} · ` : ""}
                {x.empresa}
                {x.desde ? ` · ${x.desde} – ${x.actual ? "actual" : (x.hasta ?? "?")}` : ""}
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {lectura.avisos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--warning)" }}>
          {lectura.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}

      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Los {lectura.archivos.length} archivos se adjuntarán al expediente al guardar la ficha.
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Boton tamano="sm" onClick={onIgnorar}>
          <X size={14} /> Ignorar
        </Boton>
        {lectura.se_puede_usar && (
          <Boton
            tamano="sm"
            variante="primario"
            onClick={() => onUsar(propuestaDe(lectura, marcados, conEstudios, conExperiencias))}
          >
            <Check size={14} /> Usar los datos marcados
          </Boton>
        )}
      </div>
    </section>
  )
}
